import type {HourOfTheDay, MinuteOfTheHour} from '../Time';
import type {DayOfTheWeek} from '../util/DayOfTheWeek';
import createIntegerPart from './createIntegerPart';
import createStart from './createStart';
import type {Duration, ScheduleIndex} from './ScheduleIndex';
import type {ScheduleRecords} from './ScheduleRecords';

export default function buildScheduleIndex(
  records: ScheduleRecords,
): ScheduleIndex {
  const index: ScheduleIndex = {};
  for (const record of records) {
    const day = createDayOfTheWeek(record.day);
    const hour = createHourOfTheDay(record.start.hour);
    const minute = createMinuteOfTheHour(record.start.minute);
    const duration = createDuration(record.duration);
    index[day] ??= new Map();
    const start = createStart(hour, minute);
    index[day]?.set(start, duration);
  }
  for (let i = 0; i < 7; i++) {
    const day = i as DayOfTheWeek;
    const map = index[day];
    if (!map) {
      continue;
    }
    index[day] = sortMapKeys(map, compareStrings);
  }
  return index;
}

type MapKey<T> = T extends ReadonlyMap<infer TT, unknown> ? TT : never;
type MapValue<T> = T extends ReadonlyMap<unknown, infer TT> ? TT : never;

function sortMapKeys<T extends Map<unknown, unknown>>(
  _: T,
  compare: (a: MapKey<T>, b: MapKey<T>) => number,
): Map<MapKey<T>, MapValue<T>> {
  return new Map(
    [..._.entries()].sort(([a], [b]) => {
      return compare(a as MapKey<T>, b as MapKey<T>);
    }),
  ) as Map<MapKey<T>, MapValue<T>>;
}

function compareStrings(a: string, b: string) {
  if (a < b) {
    return -1;
  } else if (a === b) {
    return 0;
  }
  return 1;
}

function createDayOfTheWeek(_: number): DayOfTheWeek {
  return createIntegerPart(_, 7) as DayOfTheWeek;
}

function createHourOfTheDay(_: number): HourOfTheDay {
  return createIntegerPart(_, 24) as HourOfTheDay;
}

function createMinuteOfTheHour(_: number): MinuteOfTheHour {
  return createIntegerPart(_, 60) as MinuteOfTheHour;
}

function createDuration(_: number): Duration {
  if (_ < 0) {
    throw new Error('Duration cannot be negative');
  }
  return Math.floor(_) as Duration;
}
